import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  Row,
  Col,
  Tag,
  Space,
  Dropdown,
  message,
  Statistic,
  Select,
  DatePicker,
  Divider,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  FileTextOutlined,
  DollarOutlined,
  CalendarOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import useStore from '../store/useStore';
import { quotesAPI, customersAPI, productsAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import FormField from '../components/forms/FormField';
import RightDrawer from '../components/common/RightDrawer';
import DetailView, { DetailSection, DetailField } from '../components/common/DetailView';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const Quotes = () => {
  const { quotes, setQuotes } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list' | 'detail'
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();

  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await quotesAPI.getAll();
      setQuotes(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [setQuotes]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await productsAPI.getAll();
      setProducts(response.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
  };

  const handleAddQuote = () => {
    setEditingQuote(null);
    form.resetFields();
    form.setFieldsValue({
      quoteDate: moment(),
      validUntil: moment().add(30, 'days'),
      status: 'draft',
      items: [{ productId: '', quantity: 1, unitPrice: 0 }],
    });
    setDrawerVisible(true);
  };

  const handleEditQuote = (quote) => {
    setEditingQuote(quote);
    form.setFieldsValue({
      ...quote,
      quoteDate: moment(quote.quoteDate),
      validUntil: moment(quote.validUntil),
    });
    setDrawerVisible(true);
  };

  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setCurrentView('detail');
  };

  const handleDeleteQuote = async (quoteId) => {
    try {
      await quotesAPI.delete(quoteId);
      message.success('Quote deleted successfully');
      await fetchQuotes();
    } catch (err) {
      message.error('Failed to delete quote');
    }
  };

  const handleSendQuote = async (quoteId) => {
    try {
      await quotesAPI.send(quoteId);
      message.success('Quote sent successfully');
      await fetchQuotes();
    } catch (err) {
      message.error('Failed to send quote');
    }
  };

  const handleConvertToOrder = async (quoteId) => {
    try {
      await quotesAPI.convertToOrder(quoteId);
      message.success('Quote converted to order successfully');
      await fetchQuotes();
    } catch (err) {
      message.error('Failed to convert quote to order');
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setEditingQuote(null);
    form.resetFields();
  };

  const handleDrawerSave = async () => {
    try {
      setDrawerLoading(true);
      const values = await form.validateFields();
      const formattedValues = {
        ...values,
        quoteDate: values.quoteDate.format('YYYY-MM-DD'),
        validUntil: values.validUntil.format('YYYY-MM-DD'),
        total: calculateTotal(values.items || []),
      };

      if (editingQuote) {
        await quotesAPI.update(editingQuote.id, formattedValues);
        message.success('Quote updated successfully');
      } else {
        await quotesAPI.create(formattedValues);
        message.success('Quote created successfully');
      }

      await fetchQuotes();
      handleDrawerClose();
    } catch (err) {
      message.error('Failed to save quote');
    } finally {
      setDrawerLoading(false);
    }
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  };

  const getQuoteStatus = (quote) => {
    const now = moment();
    const validUntil = moment(quote.validUntil);
    
    switch (quote.status) {
      case 'draft':
        return { color: 'default', text: 'Draft', icon: <EditOutlined /> };
      case 'sent':
        if (validUntil.isBefore(now)) {
          return { color: 'red', text: 'Expired', icon: <CloseCircleOutlined /> };
        }
        return { color: 'blue', text: 'Sent', icon: <SendOutlined /> };
      case 'accepted':
        return { color: 'green', text: 'Accepted', icon: <CheckCircleOutlined /> };
      case 'rejected':
        return { color: 'red', text: 'Rejected', icon: <CloseCircleOutlined /> };
      case 'converted':
        return { color: 'purple', text: 'Converted', icon: <CheckCircleOutlined /> };
      default:
        return { color: 'default', text: quote.status, icon: <ClockCircleOutlined /> };
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = quote.quoteNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
                         quote.customerName?.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      title: 'Quote #',
      dataIndex: 'quoteNumber',
      key: 'quoteNumber',
      render: (quoteNumber, record) => (
        <div>
          <div className="font-medium text-blue-600 cursor-pointer hover:underline"
               onClick={() => handleViewQuote(record)}>
            {quoteNumber}
          </div>
          <div className="text-sm text-gray-500">
            {moment(record.quoteDate).format('MMM DD, YYYY')}
          </div>
        </div>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (customerName, record) => (
        <div>
          <div className="font-medium">{customerName}</div>
          <div className="text-sm text-gray-500">{record.customerEmail}</div>
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total) => (
        <div className="font-medium">${total?.toFixed(2)}</div>
      ),
    },
    {
      title: 'Valid Until',
      dataIndex: 'validUntil',
      key: 'validUntil',
      render: (validUntil) => {
        const isExpired = moment(validUntil).isBefore(moment());
        return (
          <div className={isExpired ? 'text-red-500' : ''}>
            {moment(validUntil).format('MMM DD, YYYY')}
            {isExpired && <div className="text-xs">Expired</div>}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        const status = getQuoteStatus(record);
        return (
          <Tag color={status.color} icon={status.icon}>
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view',
            label: 'View Quote',
            icon: <FileTextOutlined />,
            onClick: () => handleViewQuote(record),
          },
          {
            key: 'edit',
            label: 'Edit Quote',
            icon: <EditOutlined />,
            onClick: () => handleEditQuote(record),
          },
        ];

        if (record.status === 'draft') {
          menuItems.push({
            key: 'send',
            label: 'Send Quote',
            icon: <SendOutlined />,
            onClick: () => handleSendQuote(record.id),
          });
        }

        if (record.status === 'accepted') {
          menuItems.push({
            key: 'convert',
            label: 'Convert to Order',
            icon: <CheckCircleOutlined />,
            onClick: () => handleConvertToOrder(record.id),
          });
        }

        menuItems.push({
          key: 'delete',
          label: 'Delete Quote',
          icon: <DeleteOutlined />,
          danger: true,
          onClick: () => {
            Modal.confirm({
              title: 'Delete Quote',
              content: 'Are you sure you want to delete this quote?',
              onOk: () => handleDeleteQuote(record.id),
            });
          },
        });

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  useEffect(() => {
    fetchQuotes();
    fetchCustomers();
    fetchProducts();
  }, [fetchQuotes, fetchCustomers, fetchProducts]);

  if (loading) {
    return <LoadingSpinner size="large" tip="Loading quotes..." />;
  }

  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={fetchQuotes}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Quotes</h1>
          <p className="text-gray-600">Manage sales quotes and estimates</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddQuote}
          className="w-full sm:w-auto"
        >
          Create Quote
        </Button>
      </div>

      {/* Stats Cards */}
      {currentView === 'list' && (
      <Row gutter={16}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Quotes"
              value={quotes.length}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Pending"
              value={quotes.filter(q => q.status === 'sent').length}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Accepted"
              value={quotes.filter(q => q.status === 'accepted').length}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Value"
              value={quotes.reduce((sum, q) => sum + (q.total || 0), 0)}
              prefix={<DollarOutlined />}
              precision={2}
            />
          </Card>
        </Col>
      </Row>
      )}

      

      

      {/* Table / Detail View */}
      <Card>
        {currentView === 'list' ? (
          <Table
            dataSource={filteredQuotes}
            columns={columns}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleViewQuote(record),
              className: 'cursor-pointer hover:bg-gray-50',
            })}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
            }}
            scroll={{ x: 800 }}
          />
        ) : (
          selectedQuote && (
            <DetailView
              title={`Quote #${selectedQuote.quoteNumber}`}
              subtitle={`${selectedQuote.customerName} • ${selectedQuote.customerEmail || ''}`}
              onBack={() => { setCurrentView('list'); setSelectedQuote(null); }}
              onEdit={() => handleEditQuote(selectedQuote)}
              onDelete={() => {
                Modal.confirm({
                  title: 'Delete Quote',
                  content: 'Are you sure you want to delete this quote?',
                  onOk: () => handleDeleteQuote(selectedQuote.id),
                });
              }}
              tags={[{ label: getQuoteStatus(selectedQuote).text, color: getQuoteStatus(selectedQuote).color }]}
            >
              <Row gutter={24}>
                <Col xs={24} lg={16} xl={16}>
                  <DetailSection title="Quote Information">
                    <DetailField label="Customer" value={selectedQuote.customerName} />
                    {selectedQuote.customerEmail && (
                      <DetailField label="Email" value={selectedQuote.customerEmail} />
                    )}
                    <DetailField label="Quote Date" value={moment(selectedQuote.quoteDate).format('MMM DD, YYYY')} />
                    <DetailField label="Valid Until" value={moment(selectedQuote.validUntil).format('MMM DD, YYYY')} />
                    <DetailField label="Status" value={getQuoteStatus(selectedQuote).text} />
                    {selectedQuote.notes && (
                      <DetailField label="Notes" value={selectedQuote.notes} />
                    )}
                  </DetailSection>
                </Col>
                <Col xs={24} lg={8} xl={8}>
                  <DetailSection title="Totals">
                    <DetailField label="Total Amount" value={`$${selectedQuote.total?.toFixed(2)}`} />
                  </DetailSection>
                </Col>
              </Row>
            </DetailView>
          )
        )}
      </Card>

      {/* Right Drawer for Create/Edit */}
      <RightDrawer
        title={editingQuote ? 'Edit Quote' : 'Create Quote'}
        open={drawerVisible}
        onClose={handleDrawerClose}
        loading={drawerLoading}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleDrawerSave} id="drawer-form">
          <Row gutter={16}>
            <Col span={12}>
              <FormField
                name="customerId"
                label="Customer"
                type="select"
                options={customers.map(c => ({ value: c.id, label: c.name }))}
                rules={[{ required: true, message: 'Please select customer' }]}
                placeholder="Select customer"
              />
            </Col>
            <Col span={12}>
              <FormField
                name="status"
                label="Status"
                type="select"
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'sent', label: 'Sent' },
                  { value: 'accepted', label: 'Accepted' },
                  { value: 'rejected', label: 'Rejected' },
                ]}
                rules={[{ required: true, message: 'Please select status' }]}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <FormField name="quoteDate" label="Quote Date" type="date" rules={[{ required: true }]} />
            </Col>
            <Col span={12}>
              <FormField name="validUntil" label="Valid Until" type="date" rules={[{ required: true }]} />
            </Col>
          </Row>

          <FormField
            name="notes"
            label="Notes"
            type="textarea"
            placeholder="Enter any additional notes"
            rows={3}
          />
        </Form>
      </RightDrawer>
    </div>
  );
};

export default Quotes;
